/**
 * Editing a nook's note (block 37) or a highlight's color (block 45) — the only
 * writes a nook accepts after capture, so the input is `UpdateNook` and nothing
 * wider.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getNookClient } from "../client.js";
import { queryKeys } from "../query-keys.js";
export function useUpdateNook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...body }) => getNookClient().updateNook(id, body),
        // Safe with no idempotency key, unlike a capture: a `PATCH` sets an
        // absolute value, so a retry after a lost response writes the same value
        // rather than a second one.
        retry: 1,
        async onMutate({ id, ...body }) {
            // Only the keys sent: an absent `userNote` must not blank the cached one.
            const patch = Object.fromEntries(Object.entries(body).filter(([, value]) => value !== undefined));
            // The whole subtree, not just the lists: an in-flight `GET /v1/nooks/:id`
            // landing after this patch would put the old note back under the cursor.
            await queryClient.cancelQueries({ queryKey: queryKeys.nooks.all() });
            const detail = queryClient.getQueryData(queryKeys.nooks.detail(id));
            const lists = queryClient.getQueriesData({
                queryKey: queryKeys.nooks.lists(),
            });
            if (detail) {
                queryClient.setQueryData(queryKeys.nooks.detail(id), {
                    ...detail,
                    ...patch,
                });
            }
            // The card's body line falls back to the note when a nook has no summary
            // and no extracted content, so the library behind the panel has to move
            // with the field the user is typing into.
            queryClient.setQueriesData({ queryKey: queryKeys.nooks.lists() }, (current) => current && {
                ...current,
                pages: current.pages.map((page) => ({
                    ...page,
                    items: page.items.map((item) => item.id === id ? { ...item, ...patch } : item),
                })),
            });
            return { detail, lists };
        },
        onError(_error, _input, context) {
            if (context?.detail) {
                queryClient.setQueryData(queryKeys.nooks.detail(context.detail.id), context.detail);
            }
            for (const [key, data] of context?.lists ?? []) {
                queryClient.setQueryData(key, data);
            }
        },
        // Written, not invalidated: the response *is* the nook, so refetching it
        // would spend a request on data already in hand — and from block 14 that
        // request is also what triggers lazy summary generation, which a note edit
        // is not (override 3).
        onSuccess(nook) {
            queryClient.setQueryData(queryKeys.nooks.detail(nook.id), nook);
        },
        // Lists still refetch: an excerpt and a card's ordering are server-derived,
        // and the patch above only guessed at one field of one row.
        onSettled() {
            void queryClient.invalidateQueries({ queryKey: queryKeys.nooks.lists() });
        },
    });
}
//# sourceMappingURL=use-update-nook.js.map